// Tema claro/escuro da aplicação inteira.
//
// O único mecanismo é a classe `dark` em <html>: tokens.css redefine as
// variáveis sob `.dark`, tailwind.config.js mapeia gray/slate/surface para
// essas variáveis, e todo componente que usa `var(--token)` acompanha.
//
// Estado é global (module-level) para que qualquer componente que chame
// useTheme() veja o mesmo valor. Persistido em localStorage; o script inline
// em public/index.html aplica a classe antes do primeiro paint para não piscar.

import { ref, computed, watch } from "vue";

export const THEME_STORAGE_KEY = "litterae:theme";
export const THEMES = ["light", "dark", "system"];

const media = window.matchMedia("(prefers-color-scheme: dark)");

function readStored() {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(v) ? v : "light";
  } catch {
    return "light";
  }
}

const theme = ref(readStored());
const systemDark = ref(media.matches);

// Enquanto `true`, a tela fica no claro qualquer que seja a preferência salva
// (que é preservada). Hoje só o Sys usa o tema escuro: App.vue liga isto para
// qualquer outro usuário e para quem não está logado.
const forceLight = ref(true);

const isDark = computed(
  () =>
    !forceLight.value &&
    (theme.value === "dark" || (theme.value === "system" && systemDark.value))
);

function apply() {
  document.documentElement.classList.toggle("dark", isDark.value);
}

let initialized = false;

export function initTheme() {
  if (initialized) return;
  initialized = true;
  media.addEventListener("change", (e) => (systemDark.value = e.matches));
  watch(isDark, apply, { immediate: true });
}

export function setTheme(value) {
  if (!THEMES.includes(value)) return;
  theme.value = value;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    /* sem storage: vale só para a sessão */
  }
}

export function setForceLight(value) {
  forceLight.value = !!value;
}

export function toggleTheme() {
  setTheme(isDark.value ? "light" : "dark");
}

export function useTheme() {
  initTheme();
  return { theme, isDark, setTheme, toggleTheme, THEMES };
}
